{
  "modules": [
    {
      "number": "01",
      "title": "Foundations",
      "isPublished": true,
      "lessons": [
        {
          "number": "1.1",
          "title": "Agentic SWE Orientation",
          "description": "This orientation is designed to provide new software engineers with the necessary knowledge and skills to succeed in their roles. It covers various topics such as company culture, software development processes",
          "duration": "8:50"
        },
        {
          "number": "1.10",
          "title": "The Agent as Orchestrator",
          "description": "Understand how purpose-based orchestrators coordinate parallel subagents to break down complex systems, and compare sequential pipelines with parallel subagent teams.",
          "duration": "5:34"
        },
        {
          "number": "1.11",
          "title": "The Problem With Vibe Coding",
          "description": "Identify the five critical weaknesses of vibe coding and understand why shipping without requirements, architecture, or tests produces fragile software.",
          "duration": "5:21"
        },
        {
          "number": "1.12",
          "title": "The 5-Phase Agentic Framework",
          "description": "Learn the five phases of the agentic framework, who owns each, and what they produce. Adapt the same pipeline to greenfield builds, new features, and bugfixes by choosing the right entry point.",
          "duration": "10:41"
        },
        {
          "number": "1.13",
          "title": "What Did We Learn?",
          "description": "Reflect on the Foundations module and solidify why agents beat one-shot prompts, disciplined phases replace vibe coding, and the framework adapts to any scenario.",
          "duration": "5:00"
        },
        {
          "number": "1.2",
          "title": "Introduction of the course",
          "description": "A three-act course on building real software with AI agents — the 5-phase framework, the skills that drive it, and the operating environment that keeps it safe.",
          "duration": "2:09"
        },
        {
          "number": "1.3",
          "title": "Course Architecture",
          "description": "A five-part progression: theory → practice → pain → cure → mastery.",
          "duration": "6:06"
        },
        {
          "number": "1.4",
          "title": "Course Architecture and Why Process Matters",
          "description": "Learn how the course is structured across five progressive parts and why a disciplined five-phase process — not unguided prompting — is the foundation of reliable AI-assisted engineering.",
          "duration": "5:26"
        },
        {
          "number": "1.5",
          "title": "Target Audience and Prerequisites",
          "description": "Identify which learner persona matches your role and what you'll gain from the course. Review the terminal, Git, and programming language prerequisites needed before starting.",
          "duration": "3:25"
        },
        {
          "number": "1.6",
          "title": "What Is an Agent?",
          "description": "Understand what an agent is and how it differs from one-shot prompts. Learn how agents decompose work into sequential skills and parallel subagents.",
          "duration": "9:04"
        },
        {
          "number": "1.7",
          "title": "What Is a Large Language Model?",
          "description": "Understand what a large language model is and how next-token prediction enables text generation, coding, and reasoning. Learn the transformer architecture and the hard limits of context windows and memory.",
          "duration": "9:32"
        },
        {
          "number": "1.8",
          "title": "How LLMs Power Agents",
          "description": "Understand how LLMs power agents through the four-phase cycle of intent, breakdown, execution, and loopback. Recognize why specific prompts produce precise results and how the framework structures the agent loop.",
          "duration": "6:24"
        },
        {
          "number": "1.9",
          "title": "Agent Execution and Loopback",
          "description": "Learn how LLMs generate working code during the execution phase and evaluate results through the loopback cycle. Recognize how agents adapt and repeat when tests fail or security issues are detected.",
          "duration": "4:59"
        }
      ]
    },
    {
      "number": "02",
      "title": "Intro to Claude Code CLI",
      "isPublished": true,
      "lessons": [
        {
          "number": "2.1",
          "title": "Claude Code: Your Agentic CLI",
          "description": "This lesson introduces Claude Code as Anthropic's official agentic CLI, demonstrating how it reads entire codebases, executes tests and shell commands, loads custom skills via slash commands, and integrates with external tools through MCP to serve as your terminal-based engineering partner for the 5-phase framework.",
          "duration": "5:12"
        },
        {
          "number": "2.2",
          "title": "The .claude/ Directory",
          "description": "This lesson maps out the `.claude/` directory structure, showing you exactly where skills, hooks, settings, rules, and agent configurations live so you can organize your project-level and global Claude Code setup without hunting for files.",
          "duration": "6:40"
        },
        {
          "number": "2.3",
          "title": "A Real ~/.claude/ — Config, Skills & Agents",
          "description": "This lesson walks through a real `~/.claude/` directory, showing you how global configuration files like `CLAUDE.md` and `settings.json`, along with `skills/`, `agents/`, and `plugins/` directories, form the active toolkit you write and invoke across every project.",
          "duration": "7:23"
        },
        {
          "number": "2.4",
          "title": "A Real ~/.claude/ — Memory, History & More",
          "description": "This lesson explores the directories Claude Code manages automatically, including persistent memory, file edit history, conversation transcripts, and cost tracking, plus how to configure custom LLM providers beyond Anthropic.",
          "duration": "7:50"
        },
        {
          "number": "2.5",
          "title": "How ~/.claude/projects/ Works",
          "description": "This lesson demonstrates how Claude Code creates isolated per-project directories inside `~/.claude/projects/`, storing session logs and project-specific memory separately from global state so every codebase gets its own tracked context.",
          "duration": "5:07"
        },
        {
          "number": "2.6",
          "title": "The Root ~/.claude.json",
          "description": "This lesson reveals the root `~/.claude.json` runtime ledger, explaining how Claude Code tracks total launches, per-project snapshots, tool permissions, MCP servers, model usage, and cost data so you can audit what the agent is actually doing.",
          "duration": "2:48"
        },
        {
          "number": "2.7",
          "title": "The Status Line — Your Live Dashboard",
          "description": "This lesson decodes Claude Code's live status line dashboard, showing you how to read model selection, context usage, real-time cost, cache hit rates, and permission modes so you can track session health and cycle between plan, default, and accept-edits modes with shift+tab.",
          "duration": "9:48"
        }
      ]
    },
    {
      "number": "03",
      "title": "Skills Intro",
      "isPublished": true,
      "lessons": [
        {
          "number": "3.1",
          "title": "Skills: Automate Your Workflows",
          "description": "Learn how to eliminate repetitive setup in every Claude Code session by creating reusable skill definitions in `.claude/skills/` — markdown-based workflow contracts that load on demand via slash commands and ensure consistent, repeatable execution across requirements gathering, architecture design, test-driven development, and code review.",
          "duration": "5:18"
        },
        {
          "number": "3.2",
          "title": "Dissecting a Skill",
          "description": "Learn how Claude Code's built-in skills work under the hood, including how natural language intent matching triggers skill invocation without slash commands, and how to discover available skills through the `/` command palette in your terminal.",
          "duration": "5:05"
        },
        {
          "number": "3.3",
          "title": "Built-in Skills: Natural Language Invocation",
          "description": "Learn how to discover and invoke Claude Code's built-in skills through both slash commands and natural language, understanding how LLM-based intent matching eliminates the need to memorize exact command syntax.",
          "duration": "3:47"
        },
        {
          "number": "3.4",
          "title": "Built-in Agents: Explore Before You Build",
          "description": "Learn how to use Claude Code's built-in read-only Explore and Plan agents to safely investigate unfamiliar codebases and design grounded architecture before writing any code, preventing costly assumptions about legacy systems.",
          "duration": "6:14"
        },
        {
          "number": "3.5",
          "title": "Demo: Automation Recommender",
          "description": "Learn how the official `claude-automation-recommender` skill analyzes your codebase to surface targeted automation recommendations across hooks, subagents, skills, plugins, and MCP servers, and see how a production-grade skill is structured by examining its SKILL.md file.",
          "duration": "11:00"
        },
        {
          "number": "3.6",
          "title": "Demo: Recommendation → Skill → Output",
          "description": "Learn how to take the automation recommender's rough starter template and turn it into a fully functional custom skill, using a real `release-notes` skill example that automatically generates, formats, and prepends changelog entries from git history.",
          "duration": "10:54"
        },
        {
          "number": "3.7",
          "title": "Demo: Let Bash Do the Work",
          "description": "Learn how to dramatically reduce token costs in your Claude Code skills by moving context gathering and curation into bash scripts, using three progressively optimized versions of a commit skill to demonstrate the principle that the LLM should read context, not gather it.",
          "duration": "15:46"
        },
        {
          "number": "3.8",
          "title": "Dynamic Context Injection",
          "description": "Learn how to dynamically inject context into your Claude Code skills to improve their performance and accuracy.",
          "duration": "5:54"
        },
        {
          "number": "3.9",
          "title": "Skill Design Patterns",
          "description": "Learn five practical design patterns that make Claude Code skills more accurate and token-efficient: bookending critical rules at the top and output format at the bottom, splitting mega-skills into workflow-specific files, referencing external documents with @path instead of pasting, using explicit output tags for precision, and writing descriptions as trigger conditions for smarter auto-invocation.",
          "duration": "6:45"
        },
        {
          "number": "3.10",
          "title": "Prompting for Precision",
          "description": "Learn four practical rules for writing precise prompts in Claude Code: be explicit instead of conversational, investigate before answering, stay within requested scope, and match thinking depth to task complexity. These rules transform vague instructions into disciplined engineering that prevents hallucinations, scope drift, and wasted tokens.",
          "duration": "8:21"
        },
        {
          "number": "3.11",
          "title": "Skills: Further Study",
          "description": "Explore advanced skill features beyond the basics: frontmatter fields for fine-tuning behavior, argument passing and built-in substitutions for dynamic skills, directory structure for bundling supporting files, and built-in commands like /run, /verify, and /doctor for workflow automation and diagnostics. This lesson maps the deeper skill system surfaces documented in the official Claude Code docs, pointing you toward self-directed exploration.",
          "duration": "4:00"
        }
      ]
    },
    {
      "number": "04",
      "title": "Under the hood",
      "isPublished": true,
      "lessons": [
        {
          "number": "4.1",
          "title": "Under the hood",
          "description": "Pull back the hood to learn how tokens, context windows, statelessness, and caching drive every agent interaction — from why long sessions get expensive to how a warm prefix makes the same task over three times faster and cheaper. You'll leave with practical working habits that replace magic with mechanics, keeping your sessions lean and your costs down.",
          "duration": "1:22"
        },
        {
          "number": "4.2",
          "title": "What Is a Token?",
          "description": "Learn what tokens are — the sub-word units that LLMs actually process instead of words or letters — and understand why token count is the single metric that governs context size, speed, and cost for every prompt, file read, and model output.",
          "duration": "5:36"
        },
        {
          "number": "4.3",
          "title": "What's in the Context Window",
          "description": "This lesson reveals what actually fills the model's context window before you type a single word — system prompts, tool definitions, CLAUDE.md, skills, files, and prior conversation — and explains why every one of those pre-loaded tokens competes for the same finite budget you're paying for on each turn.",
          "duration": "8:07"
        },
        {
          "number": "4.4",
          "title": "The Server Remembers Nothing",
          "description": "You will learn why the LLM server is fundamentally stateless — it remembers nothing between calls — and how Claude Code manages the entire conversation context on your local machine, re-sending the full payload every single time.",
          "duration": "5:37"
        },
        {
          "number": "4.5",
          "title": "The Loop: How Tools Run",
          "description": "In this lesson, you will learn how the agentic tool loop works step by step: the model cannot run anything itself, so it requests tools, Claude Code executes them, appends the results to the context, and resends everything in a loop until the task is done. You will also understand why a single request can trigger many hidden turns and why the context window grows more expensive with each one.",
          "duration": "5:10"
        },
        {
          "number": "4.6",
          "title": "Why Sessions Get Expensive",
          "description": "Learn why agent sessions get exponentially expensive as they go deeper: every turn re-sends and re-computes the entire growing context window from scratch, meaning you rebill the same stable tokens — system prompts, tools, and early turns — dozens of times over.",
          "duration": "3:09"
        },
        {
          "number": "4.7",
          "title": "One Request, End to End",
          "description": "This lesson maps the complete journey of one LLM request from client to inference server, walking through tokenization, prefill, and the serial decode loop to reveal why statelessness and the KV cache are the hidden drivers of cost and performance.",
          "duration": "6:47"
        },
        {
          "number": "4.8",
          "title": "Why It's Expensive: Tokenize, Prefill, Decode",
          "description": "This lesson breaks down the three hidden stages inside every LLM request — tokenize, prefill, and decode — and reveals why the bulk of the cost comes from re-prefilling your entire unchanged context from scratch on every single turn.",
          "duration": "5:13"
        },
        {
          "number": "4.9",
          "title": "Live: Prefill vs. Decode",
          "description": "This live demo on a local LLM reveals the dramatic speed difference between prefill and decode: prefill ingests the entire prompt in parallel at high throughput, while decode generates each output token serially — exposing the fundamental bottleneck that makes generation inherently slower than ingestion.",
          "duration": "10:17"
        },
        {
          "number": "4.10",
          "title": "KV Cache: Compute Once, Reuse",
          "description": "In this lesson, you'll learn how KV caching works under the hood: each token's Key and Value vectors are computed once during prefill, stored in a cache, and reused for every subsequent decode step — turning what would be a wasteful quadratic recomputation into an efficient linear lookup. You'll also see why the cache is ephemeral and gets wiped at the end of each response, and how this relates to the broader cost problem of re-sending context on every turn.",
          "duration": "7:06"
        },
        {
          "number": "4.11",
          "title": "Inside the Lookup: How Attention Picks",
          "description": "This lesson walks through the three-step attention mechanism — scoring Query-Key matches with dot-product, applying softmax to get attention weights, and blending Value vectors — using a real sentence to show how the model resolves ambiguous pronoun references. You'll learn why attention is called a \"soft dictionary lookup\" and why the entire token history must stay in the KV cache for every lookup to work.",
          "duration": "5:38"
        },
        {
          "number": "4.12",
          "title": "Where the KV Cache Lives",
          "description": "This lesson maps the inference server into three distinct boxes — weights, KV cache store, and forward pass — to show that the KV cache lives in a separate memory region, not inside the model, which is exactly why it can outlive one request.",
          "duration": "7:42"
        },
        {
          "number": "4.13",
          "title": "The Boxes, Live in oMLX",
          "description": "You will see the three abstract memory boxes — Weights, KV Store, and Forward Pass — mapped to real, live dashboard panels in oMLX running locally on a Mac Mini, making the inference server's unified memory allocation concrete and measurable. This live demo bridges theory and practice by showing exactly how ~7 GB of weights, ~16 GB of runtime KV cache, and ~6 GB of working buffers coexist in actual hardware.",
          "duration": "4:58"
        },
        {
          "number": "4.14",
          "title": "Live Demo: oMLX + Gemma-4",
          "description": "Watch a live walkthrough of the oMLX inference server running Gemma-4 on a Mac Mini M4 Pro, where you'll see prefill and decode in action alongside the three memory regions — weights, KV cache store, and forward pass — updating in real time on the dashboard. This hands-on demo connects the theory of token ingestion, KV cache reuse, and attention mechanics to what actually happens inside a running local LLM.",
          "duration": "6:45"
        },
        {
          "number": "4.15",
          "title": "What Caching Actually Skips",
          "description": "This lesson clears up a common trap: prompt caching does not skip tokenization, which always runs on CPU. Instead, it bypasses the expensive GPU forward-pass K/V computation for unchanged prefixes—the only stage that was ever truly costly.",
          "duration": "4:48"
        },
        {
          "number": "4.16",
          "title": "Prompt Caching: Same Store, Kept Warm",
          "description": "Learn how prompt caching reuses the same KV store across requests by keeping prefix key-values alive, allowing identical prefixes to skip expensive prefill and cost only a fraction on cache hits.",
          "duration": "6:32"
        },
        {
          "number": "4.17",
          "title": "Cache Reuse Over Time",
          "description": "This lesson traces the full lifecycle of a KV cache entry from first write through repeated hits to eventual eviction. You will learn how prefix-matched cache entries deliver cheap reuse across turns with a refreshed TTL each time, and how either an early token change or idle expiration forces the server to pay full prefill cost to rebuild the entry from scratch.",
          "duration": "4:24"
        },
        {
          "number": "4.18",
          "title": "What Is Your Stable Prefix?",
          "description": "In this lesson, you will learn how prompt caching works by understanding the difference between your stable prefix — system prompts, CLAUDE.md, and tool definitions that get cached after the first turn — and your volatile conversation tail that is re-billed every turn, so you can write persistent rules in the right place to minimize costs.",
          "duration": "4:01"
        },
        {
          "number": "4.19",
          "title": "Live: Without Cache",
          "description": "Watch a live experiment where cache is disabled on an oMLX inference server, revealing how every turn recomputes the entire context from scratch—burning 224K prefill tokens, $1.44 in API costs, and over ten minutes for a ten-turn session with zero reuse. This lesson delivers the hard numbers that prove why prompt caching matters and what you are really paying for when the KV store is cold.",
          "duration": "6:53"
        },
        {
          "number": "4.20",
          "title": "Spend Fewer Tokens, Do More",
          "description": "Learn four practical habits to minimize token costs by structuring your prompts around how LLM context windows and prompt caching actually work: keep stable content upfront, avoid pre-loading unnecessary context, maintain a lean window, and account for heavier tokenization in non-Latin scripts. This lesson translates the mechanics of caching and stateless inference into concrete, money-saving working habits.",
          "duration": "3:44"
        }
      ]
    }
  ]
}
